import { useState, useEffect, useMemo } from 'react';
import { Card, Tree, Space, Checkbox, Button } from 'antd';
import {
    LoadingOutlined,
    DeleteOutlined,
    FileOutlined,
    InboxOutlined,
    EditOutlined,
    BoxPlotOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../../hooks/useMainContext';
import { FaExpandAlt } from 'react-icons/fa';
import './SelectedPageList.css';
import {
    TbLayoutSidebarLeftExpand,
    TbLayoutSidebarRightExpand,
} from 'react-icons/tb';
import { message } from 'antd';

const GreenCheckIcon = () => (
    <span style={{ color: 'green', fontSize: 16 }}>✔️</span>
);

const EditableClosetTitle = ({
    value,
    closetId,
    closetManager,
    viewManager,
    drawingManager,
    setSelectedUnitKey,
    unitId,
}) => {
    const handleDelete = () => {
        if (drawingManager.isDrawingModeOn) {
            message.error("Can't do this operation while drawing mode is on.");
            return;
        }
        closetManager.removeClosetById(closetId);
        closetManager.drawNewClosets();
    };

    const handleFitToScreen = async () => {
        setSelectedUnitKey(`unit-${unitId}`);
        await closetManager.showClosets(closetId);
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}>
            <Space>
                <span>{value}</span>
                <EditOutlined
                    style={{ color: 'blue', cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        viewManager.setClosetForm(true);
                        closetManager.setSelectedClosetById(closetId);
                    }}
                />
                <DeleteOutlined
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={handleDelete}
                />
                <FaExpandAlt
                    style={{
                        color: 'green',
                        cursor: 'pointer',
                        fontSize: '16px',
                    }}
                    onClick={handleFitToScreen}
                />
            </Space>
        </div>
    );
};

const convertToTreeData = (
    data,
    closets,
    attachments,
    statusArray,
    closetManager,
    viewManager,
    drawingManager,
    setSelectedUnitKey,
) => {
    const effectiveSelectedPage =
        closetManager.selectedPageNo != null
            ? closetManager.selectedPageNo
            : data.length > 0
              ? data[0].pageNum
              : null;

    return [...data]
        .sort((a, b) => Number(a.pageNum) - Number(b.pageNum))
        .map((item) => {
            const pageClosets = closets.filter(
                (closet) => Number(closet._pageNo) === Number(item.pageNum),
            );
            const isProcessed = statusArray.some(
                (status) => status.pageNo == item.pageNum && status.status,
            );

            return {
                title: (
                    <Space>
                        <FileOutlined />
                        {item.name || `Page ${item.pageNum}`}
                        {isProcessed ? (
                            <GreenCheckIcon />
                        ) : (
                            <LoadingOutlined style={{ color: 'orange' }} />
                        )}
                    </Space>
                ),
                key: `page-${item.pageNum}`,
                pageNumber: item.pageNum,
                selected:
                    Number(item.pageNum) === Number(effectiveSelectedPage),
                children: pageClosets.length
                    ? pageClosets.map((closet, closetIndex) => {
                          const closetAttachments = attachments.filter(
                              (att) => att.closetId === closet._id,
                          );
                          const unitId = closetManager.getUnitIdByClosetId(
                              closet._id,
                          );

                          return {
                              title: (
                                  <Space>
                                      <InboxOutlined />
                                      <EditableClosetTitle
                                          value={
                                              closet.name ||
                                              `Closet ${closetIndex + 1}`
                                          }
                                          closetId={closet._id}
                                          closetManager={closetManager}
                                          viewManager={viewManager}
                                          drawingManager={drawingManager}
                                          setSelectedUnitKey={
                                              setSelectedUnitKey
                                          }
                                          unitId={unitId}
                                      />
                                  </Space>
                              ),
                              key: `page-${item.pageNum}-closet-${closetIndex}`,
                              children: closetAttachments.length
                                  ? closetAttachments.map((att, attIndex) => ({
                                        title: (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                <span
                                                    style={{
                                                        cursor: 'default',
                                                    }}>
                                                    {att._attachmentType ||
                                                        'Unknown'}{' '}
                                                    - {att._type || 'N/A'} -{' '}
                                                    {att._opening || 'N/A'}
                                                </span>
                                                <Space>
                                                    <EditOutlined
                                                        style={{
                                                            color: 'blue',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            closetManager.setSelectedAttachment(
                                                                att,
                                                            );
                                                            closetManager.setAttachmentEditMode(
                                                                true,
                                                            );
                                                        }}
                                                    />
                                                    <DeleteOutlined
                                                        style={{
                                                            color: 'red',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={(e) => {
                                                            if (
                                                                drawingManager.isDrawingModeOn
                                                            ) {
                                                                message.error(
                                                                    "Can't do this operation while drawing mode is on.",
                                                                );
                                                                return;
                                                            }
                                                            e.stopPropagation();
                                                            closetManager.removeAttachmentById(
                                                                att.id,
                                                            );
                                                        }}
                                                    />
                                                </Space>
                                            </div>
                                        ),
                                        key: `page-${item.pageNum}-closet-${closetIndex}-att-${attIndex}`,
                                    }))
                                  : [
                                        {
                                            title: 'No attachments',
                                            key: `page-${item.pageNum}-closet-${closetIndex}-empty`,
                                        },
                                    ],
                          };
                      })
                    : [
                          {
                              title: 'No closets available',
                              key: `page-${item.pageNum}-empty`,
                          },
                      ],
            };
        });
};

const convertUnitsToTreeData = (
    units,
    viewManager,
    drawingManager,
    closetManager,
    checkedUnits,
    handleCheckboxChange,
    setSelectedUnitKey,
) =>
    units.length
        ? units.map((unit) => {
              const unitChildren =
                  viewManager.matrixCalculation && closetManager.presentUnits
                      ? Object.entries(closetManager.presentUnits)
                            .filter(([category]) => category === unit.name)
                            .flatMap(([category, unitList]) => {
                                const unitsByPage = unitList.reduce(
                                    (acc, presentUnit) => {
                                        const pageNo = presentUnit.pageNo;
                                        if (!acc[pageNo]) {
                                            acc[pageNo] = [];
                                        }
                                        acc[pageNo].push(presentUnit);
                                        return acc;
                                    },
                                    {},
                                );

                                return Object.entries(unitsByPage).flatMap(
                                    ([pageNo, pageUnits]) =>
                                        pageUnits.map(
                                            (presentUnit, unitIndex) => {
                                                const unitKey = `unit-${unit._id}-present-${pageNo}-${unitIndex}`;
                                                return {
                                                    key: unitKey,
                                                    title: (
                                                        <Space>
                                                            <Checkbox
                                                                checked={checkedUnits.has(
                                                                    unitKey,
                                                                )}
                                                                onChange={(e) =>
                                                                    handleCheckboxChange(
                                                                        unitKey,
                                                                        e.target
                                                                            .checked,
                                                                        unit.name,
                                                                    )
                                                                }
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }
                                                            />
                                                            <span
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: 'blue',
                                                                }}
                                                                onClick={async (
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    await closetManager.setSelectedPageNo(
                                                                        Number(
                                                                            presentUnit.pageNo,
                                                                        ),
                                                                    );
                                                                    closetManager.showPresentUnits(
                                                                        presentUnit.uuid,
                                                                    );
                                                                }}>
                                                                {category} :
                                                                (Page{' '}
                                                                {
                                                                    presentUnit.pageNo
                                                                }{' '}
                                                                - Instance{' '}
                                                                {unitIndex + 1})
                                                            </span>
                                                        </Space>
                                                    ),
                                                    disabled:
                                                        !viewManager.matrixCalculation,
                                                };
                                            },
                                        ),
                                );
                            })
                      : [];

              return {
                  key: `unit-${unit._id}`,
                  title: (
                      <div
                          style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                          }}>
                          <Space>
                              <BoxPlotOutlined />
                              {unit.name || 'Unnamed'} [ Page {unit._pageNo}]
                          </Space>
                          <Space>
                              <EditOutlined
                                  style={{ color: 'blue', cursor: 'pointer' }}
                                  onClick={(e) => {
                                      if (viewManager.matrixCalculation) return;
                                      e.stopPropagation();
                                      closetManager.setSelectedUnit(unit);
                                      viewManager.setUnitForm(true);
                                  }}
                              />
                              <DeleteOutlined
                                  style={{ color: 'red', cursor: 'pointer' }}
                                  onClick={(e) => {
                                      if (drawingManager.isDrawingModeOn) {
                                          message.error(
                                              "Can't do this operation while drawing mode is on.",
                                          );
                                          return;
                                      }
                                      if (viewManager.matrixCalculation) return;
                                      e.stopPropagation();
                                      closetManager.removeUnitById(unit._id);
                                  }}
                              />
                              <FaExpandAlt
                                  style={{
                                      color: 'green',
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                  }}
                                  onClick={async (e) => {
                                      e.stopPropagation();
                                      setSelectedUnitKey(`unit-${unit._id}`);
                                      await closetManager.showUnits(unit._id);
                                  }}
                              />
                          </Space>
                      </div>
                  ),
                  children: unitChildren,
              };
          })
        : [{ key: 'units-empty', title: 'No units available', disabled: true }];

export const SelectedPageList = observer(({ selectedPages }) => {
    const { viewManager, closetManager, drawingManager } = useMainContext();
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [isActive, setIsActive] = useState(true);
    const [checkedUnits, setCheckedUnits] = useState(new Set());
    const [selectedUnitKey, setSelectedUnitKey] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleCheckboxChange = (unitKey, checked, unitName) => {
        setCheckedUnits((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(unitKey);
            } else {
                newSet.delete(unitKey);
            }

            const parts = unitKey.split('-');
            const pageNumber = parts[3];
            const instanceIndex = parts[4];
            const combinedKey = `${pageNumber}-${instanceIndex}`;
            const checkedCount = Array.from(newSet).filter((key) =>
                key.startsWith(parts.slice(0, 2).join('-')),
            ).length;
            closetManager.updateUnitQtyByName(unitName, checkedCount);
            closetManager.updateUnitPagesByName(unitName, combinedKey, checked);

            return newSet;
        });
    };

    const treeData = useMemo(
        () =>
            convertToTreeData(
                selectedPages,
                closetManager.closets,
                closetManager.attachments,
                viewManager.pageStatus,
                closetManager,
                viewManager,
                drawingManager,
                setSelectedUnitKey,
            ),
        [
            selectedPages,
            closetManager.closets,
            closetManager.attachments,
            viewManager.pageStatus,
            closetManager.selectedPageNo,
        ],
    );

    const unitTreeData = useMemo(
        () =>
            convertUnitsToTreeData(
                closetManager.units,
                viewManager,
                drawingManager,
                closetManager,
                checkedUnits,
                handleCheckboxChange,
                setSelectedUnitKey,
            ),
        [
            closetManager.units,
            viewManager,
            closetManager,
            checkedUnits,
            viewManager.matrixCalculation,
            closetManager.isLoading,
        ],
    );

    useEffect(() => {
        if (!isActive) return;

        const interval = setInterval(async () => {
            await closetManager.getProcessedSnappingPoints();
            if (
                viewManager.selectedPages.length ===
                    viewManager.pageStatus.length &&
                !viewManager.pageStatus.some((page) => !page.status)
            ) {
                clearInterval(interval);
                setIsActive(false);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [isActive, closetManager, viewManager]);

    const selectedPageKeys = useMemo(() => {
        return treeData.filter((node) => node.selected).map((node) => node.key);
    }, [treeData]);

    const toggleSize = () => {
        setIsExpanded((prev) => !prev);
    };

    return (
        <Card
            className={`selected-page-list ${isExpanded ? 'expanded' : 'shrunk'}`}
            style={{
                width: isExpanded ? '400px' : '40px',
                maxHeight: isExpanded ? '90vh' : '40px',
                overflow: 'hidden',
            }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginBottom: isExpanded ? '10px' : '0',
                    padding: isExpanded ? '5px 10px' : '0',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    position: 'absolute',
                    right: isExpanded ? '0px' : '0',
                }}>
                <Button
                    type="text"
                    icon={
                        isExpanded ? (
                            <TbLayoutSidebarRightExpand />
                        ) : (
                            <TbLayoutSidebarLeftExpand />
                        )
                    }
                    onClick={toggleSize}
                    style={{
                        fontSize: '20px',
                        padding: 0,
                        margin: 0,
                        width: '38px',
                    }}
                />
            </div>
            {isExpanded && (
                <div className="content-container w-full">
                    <div style={{ marginBottom: '20px' }}>
                        <h3>📄 Pages</h3>
                        <Tree
                            onExpand={setExpandedKeys}
                            expandedKeys={expandedKeys}
                            treeData={treeData}
                            className="custom-tree"
                            onSelect={async (selectedKeys, info) => {
                                if (info.node.pageNumber !== undefined) {
                                    closetManager.setIsLoading(true);
                                    viewManager.setActivePageNo(
                                        info.node.pageNumber,
                                    );
                                    await viewManager.bringChangesToClosetAccordingToSelectedPages();
                                    await closetManager.setSelectedPageNo(
                                        info.node.pageNumber,
                                    );
                                    await closetManager.fitToScreen();
                                    viewManager.updatePossibleClosets();
                                    closetManager.setIsLoading(false);
                                }
                            }}
                            selectedKeys={selectedPageKeys}
                            defaultExpandAll={true}
                        />
                    </div>

                    <div>
                        <h3>🏠 Units</h3>
                        <Tree
                            onExpand={setExpandedKeys}
                            expandedKeys={expandedKeys}
                            treeData={unitTreeData}
                            className="custom-tree"
                            selectedKeys={
                                selectedUnitKey ? [selectedUnitKey] : []
                            }
                        />
                    </div>
                </div>
            )}
        </Card>
    );
});
